import React from 'react';
import { X, FileText, Save, FolderOpen, Download, Upload, Library, HelpCircle, Settings, FilePlus } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { useLanguage } from '@/app/contexts/LanguageContext';
import { emitTutorialEvent } from '@/app/contexts/TutorialContext';
import { RestrictedFeature } from '@/app/components/RestrictedFeature';
import { PERMISSIONS } from '@/app/constants/plans';

interface NavigationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onNewFile: () => void;
  onSave: () => void;
  onSaveAs?: () => void;
  onLoad: () => void;
  onExport: () => void;
  onImport: () => void;
  onOpenLibrary: () => void;
  onOpenHelp: () => void;
  onOpenSettings: () => void;
}

export function NavigationDrawer({
  isOpen,
  onClose,
  onNewFile,
  onSave,
  onSaveAs,
  onLoad,
  onExport,
  onImport,
  onOpenLibrary,
  onOpenHelp,
  onOpenSettings,
}: NavigationDrawerProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay - transparent backdrop */}
      <div
        className="fixed inset-0 bg-transparent z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="fixed top-0 left-0 h-full w-80 z-50 transition-transform duration-300 ease-in-out"
        style={{
          backgroundColor: 'var(--md-surface-container-low)',
          boxShadow: 'var(--md-elevation-3)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <h2 className="text-xl font-medium" style={{ color: 'var(--md-on-surface)' }}>
            IsoCAD
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-full w-10 h-10 p-0"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation Items */}
        <nav className="p-2">
          <div className="space-y-1">
            {/* File Section */}
            <div className="px-3 py-2">
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--md-on-surface-variant)' }}>
                {t('menu.file')}
              </p>
            </div>

            <button
              onClick={() => {
                onNewFile();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
              style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FileText className="h-5 w-5" />
              <span className="font-medium">{t('common.newFile')}</span>
            </button>

            <RestrictedFeature permission={PERMISSIONS.SAVE_PROJECT} className="w-full">
              <button
                onClick={() => {
                  onSave();
                  onClose();
                  emitTutorialEvent({ type: 'menu-opened', menu: 'save' });
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
                style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                data-tutorial="save-part-btn"
              >
                <Save className="h-5 w-5" />
                <span className="font-medium">{t('common.save')}</span>
              </button>
            </RestrictedFeature>

            <RestrictedFeature permission={PERMISSIONS.SAVE_PROJECT} className="w-full">
              {onSaveAs && (
                <button
                  onClick={() => {
                    onSaveAs();
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
                  style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <FilePlus className="h-5 w-5" />
                  <span className="font-medium">{t('common.saveAs')}</span>
                </button>
              )}
            </RestrictedFeature>

            <button
              onClick={() => {
                onLoad();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
              style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FolderOpen className="h-5 w-5" />
              <span className="font-medium">{t('common.load')}</span>
            </button>

            {/* Import/Export Section */}
            <div className="px-3 py-2 mt-4">
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--md-on-surface-variant)' }}>
                {t('menu.importExport')}
              </p>
            </div>

            <RestrictedFeature permission={PERMISSIONS.EXPORT_PDF} className="w-full">
              <button
                onClick={() => {
                  onExport();
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
                style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Download className="h-5 w-5" />
                <span className="font-medium">{t('common.exportSvg')}</span>
              </button>
            </RestrictedFeature>

            <button
              onClick={() => {
                onImport();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
              style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Upload className="h-5 w-5" />
              <span className="font-medium">{t('common.importPart')}</span>
            </button>

            {/* Tools Section */}
            <div className="px-3 py-2 mt-4">
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--md-on-surface-variant)' }}>
                {t('menu.tools')}
              </p>
            </div>

            <button
              onClick={() => {
                onOpenLibrary();
                onClose();
                emitTutorialEvent({ type: 'menu-opened', menu: 'library' });
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
              style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              data-tutorial="parts-library-toggle"
            >
              <Library className="h-5 w-5" />
              <span className="font-medium">{t('common.symbolLibrary')}</span>
            </button>

            <button
              onClick={() => {
                onOpenSettings();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
              style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Settings className="h-5 w-5" />
              <span className="font-medium">{t('common.backgroundSettings')}</span>
            </button>

            {/* Help Section */}
            <div className="px-3 py-2 mt-4">
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--md-on-surface-variant)' }}>
                {t('menu.help')}
              </p>
            </div>

            <button
              onClick={() => {
                onOpenHelp();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-opacity-80 transition-colors"
              style={{ backgroundColor: 'transparent', color: 'var(--md-on-surface)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--md-secondary-container)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <HelpCircle className="h-5 w-5" />
              <span className="font-medium">{t('common.helpAndShortcuts')}</span>
            </button>
          </div>
        </nav>
      </div>
    </>
  );
}